import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { Input } from '../../../Components/UI/input';
import { Label } from '../../../Components/UI/label';
import { X, ArrowLeft, AlertTriangle, Check } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { sendResetOTPAPI, resetAgencyDataAPI } from './ResetDataAPI';
import { toast } from 'sonner';
import { setGlobalLoading } from '../../../Store/Actions';
import { checkIfInqOnlyMode, getAgencyDetails, getUsersArr } from '../../../Store/Reducers';
import { verifyResetOTP } from './ResetDataAPI';

const CONFIRMATION_TEXT = "Please delete my SahiClub data";

const ResetDataDialog = ({ open, onClose }) => {
    const [step, setStep] = useState(1); // 1: Warning, 2: OTP, 3: Confirmation
    const [otp, setOtp] = useState('');
    const [confirmationText, setConfirmationText] = useState('');
    const [hasResent, setHasResent] = useState(false);

    const dispatch = useDispatch();
    const usersArr = useSelector(getUsersArr);

	  const agencyData = useSelector(getAgencyDetails);
	  const isCtrcsEnabled = agencyData?.EnblContracts;
	  const isXnsEnabled = agencyData?.EnblXns;
	  
	  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);
	

    // Get admin user email from users array
    const adminEmail = useMemo(() => {
        const adminUser = usersArr.find(user => user.isAdmin === true);
        return adminUser?.email || '';
    }, [usersArr]);

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setStep(1);
            setOtp('');
            setConfirmationText('');
            setHasResent(false);
        }
    }, [open]);

    const handleClose = () => {
        setStep(1);
        setOtp('');
        setConfirmationText('');
        setHasResent(false);
        onClose();
    };

    const handleOpenChange = (isOpen) => {
        if (!isOpen) {
            handleClose();
        }
    };

    // Step 1: Send OTP
    const handleSendOTP = async () => {
        if (!adminEmail) {
            toast.error('Admin email not found. Please contact support.');
            return;
        }

        dispatch(setGlobalLoading(true));
        try {
            const response = await sendResetOTPAPI();
            if (response.error === false) {
                toast.success(response.message || `OTP sent to ${adminEmail}`);
                setStep(2);
            } else {
                toast.error(response.message || 'Failed to send OTP.');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to send OTP.');
        } finally {
            dispatch(setGlobalLoading(false));
        }
    };

    // Step 2: Verify OTP
    // const handleVerifyOTP = () => {
    //     if (!otp || otp.trim().length === 0) {
    //         toast.error('Please enter the OTP.');
    //         return;
    //     }
    //     setStep(3);
    // };

	// Step 2: Verify OTP
	const handleVerifyOTP = async () => {
		if (!otp || otp.trim().length === 0) {
			toast.error('Please enter the OTP.');
			return;
		}

		dispatch(setGlobalLoading(true));
		try {
			const response = await verifyResetOTP(otp);
			if (response.error === false) {
				toast.success(response.message || 'OTP verified.');
				setStep(3);
			} else {
				toast.error(response.message || 'Invalid OTP. Please try again.');
				setOtp('');
			}
		} catch (error) {
			toast.error(error.message || 'Failed to verify OTP.');
			setOtp('');
		} finally {
			dispatch(setGlobalLoading(false));
		}
	};

    // Resend OTP
    const handleResendOTP = async () => {
        if (hasResent) return;

        if (!adminEmail) {
            toast.error('Admin email not found. Please contact support.');
            return;
        }

        dispatch(setGlobalLoading(true));
        try {
            const response = await sendResetOTPAPI(adminEmail);
            if (response.error === false) {
                setHasResent(true);
                toast.success(response.message || 'OTP resent');
            } else {
                toast.error(response.message || 'Failed to resend OTP.');
            }
        } catch (error) {
            toast.error(error.message || 'Failed to resend OTP.');
        } finally {
            dispatch(setGlobalLoading(false));
        }
    };

    // Step 3: Final confirmation and reset
    const handleResetData = async () => {
        if (confirmationText.trim() !== CONFIRMATION_TEXT) {
            toast.error('Confirmation text does not match. Please type exactly as shown.');
            return;
        }

        dispatch(setGlobalLoading(true));
        try {
            const response = await resetAgencyDataAPI(otp);
            if (response.error === false) {
                toast.success('Your data has been reset.');
                handleClose();
                localStorage.removeItem('MyLastSyncDT');
                localStorage.removeItem('getIDB');
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                toast.error(response.message || 'Failed to reset data.');
                setOtp('');
                setConfirmationText('');
                setStep(2);
            }
        } catch (error) {
            toast.error(error.message || 'Failed to reset data.');
            setOtp('');
            setConfirmationText('');
            setStep(2);
        } finally {
            dispatch(setGlobalLoading(false));
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="lg:!max-w-[28vw] max-w-[100vw]">
                <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
                    <div className="sm:hidden flex items-center justify-between w-full h-8">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center" onClick={handleClose}>
                                <ArrowLeft className="h-6 w-6" />
                                <span className="sr-only">Back</span>
                            </DialogClose>
                            <DialogTitle className="text-lg font-medium">
                                <div className="truncate">Reset Data</div>
                            </DialogTitle>
                        </div>
                    </div>
                    <DialogTitle className="hidden sm:block text-lg md:text-xl">
                        Reset Data
                    </DialogTitle>
                    <div className="hidden sm:flex items-center gap-2">
                        <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={handleClose}>
                            <X className="h-5 w-5" />
                            <span className="sr-only">Close</span>
                        </DialogClose>
                    </div>
                </DialogHeader>

                {/* Scrollable middle section */}
                <div className="lg:max-h-[70vh] max-h-[90vh] overflow-y-auto px-4">
                    {/* Step 1: Warning */}
                    {step === 1 && (
                        <div className="space-y-4">
                            <div className="flex items-start gap-3 p-4 bg-sahi-red-light border border-sahi-red rounded-lg">
                                <AlertTriangle className="h-5 w-5 text-sahi-red flex-shrink-0 mt-0.5" />
                                <div className="space-y-2">
                                    <p className="text-sm font-medium text-sahi-red">
                                        Warning: This action cannot be undone!
                                    </p>
                                    <p className="text-sm text-gray-800">
										All your data including contacts,
										{isInqOnlyMode ? " inquiries" : " threads"},
										tasks
										{isXnsEnabled && ", transactions"}
										{isCtrcsEnabled && ", contracts"}
										, interactions etc. will be permanently deleted. Only User list will be retained.
										This data cannot be recovered once reset.
									</p>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <p className="text-sm text-gray-700">
                                    Are you sure you want to continue?
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Step 2: OTP Verification */}
                    {step === 2 && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <p className="text-sm text-gray-700">
                                    We have sent an OTP to your registered email: <span className="font-medium">{adminEmail}</span>
                                </p>
                                <p className="text-sm text-gray-600">
                                    Please enter the OTP to proceed to delete your data.
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="otp" className="text-sm font-medium" required={true}>
                                    Enter OTP
                                </Label>
                                <div className="flex gap-2 items-center">
									<Input
										id="otp"
										type="text"
										placeholder="Enter OTP"
										value={otp}
										onChange={(e) => {
											const numericValue = e.target.value.replace(/[^0-9]/g, '');
											setOtp(numericValue);
										}}
										className="flex-1 sahi-input"
										autoComplete="off"
										inputMode="numeric"
										maxLength={6}
									/>
									{!hasResent && (
										<div className="w-[80px] items-center">
											<Button
												variant="secondary"
												onClick={handleResendOTP}
												disabled={false}
												className="w-full whitespace-nowrap px-2"
												type="button"
											>
												Resend
											</Button>
										</div>
									)}
								</div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Final Confirmation */}
                    {step === 3 && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <p className="text-sm text-gray-700">
                                    To confirm the reset, please type the following text exactly (To verify that you're not drunk 😄):
                                </p>
                                <div className="p-3 bg-gray-50 rounded-md">
                                    <code className="text-sm font-mono text-gray-900 select-none">{CONFIRMATION_TEXT}</code>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="confirmationText" className="text-sm font-medium" required={true}>
                                    Confirmation Text
                                </Label>
                                <Input
                                    id="confirmationText"
                                    type="text"
                                    placeholder="Type the confirmation text"
                                    value={confirmationText}
                                    onChange={(e) => setConfirmationText(e.target.value)}
                                    className="w-full sahi-input"
                                    autoComplete="off"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer with buttons */}
                <div className="flex items-center justify-start gap-3 lg:px-4 px-4 lg:pb-4 pb-6 pt-0 border-gray-200">
                    {step === 1 && (
                        <>
                            <Button
                                onClick={handleSendOTP}
                                className="w-auto bg-sahi-red border-sahi-red hover:text-white hover:border-sahi-red hover:text-sahi-red"
                            >
                                <Check className="h-4 w-4" />
                                Yes, Continue
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={handleClose}
                            >
                                <X className="h-4 w-4 text-sahi-primary-color cursor-pointer" />
                                No, Cancel
                            </Button>
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <Button
                                onClick={handleVerifyOTP}
                                className="w-auto"
                                disabled={!otp || otp.length < 4}
                            >
                                Verify OTP
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                        </>
                    )}

                    {step === 3 && (
                        <>
                            <Button
                                onClick={handleResetData}
                                className="w-auto bg-sahi-red border-sahi-red hover:text-white hover:border-sahi-red hover:text-sahi-red"
                                disabled={confirmationText.trim() !== CONFIRMATION_TEXT}
                            >
                                <Check className="h-4 w-4" />
                                Reset Data
                            </Button>
                            <Button
                                variant="secondary"
                                onClick={handleClose}
                            >
                                <X className="h-4 w-4 text-sahi-primary-color cursor-pointer" />
                                Cancel
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ResetDataDialog;
